import type { MeasurementEvent } from "@grafana/faro-web-sdk";

import { captureException } from "@/error/sentry";
import type { ClientSettings } from "@/store/clientSettings";
import { useClientSettingsStore } from "@/store/clientSettings";

import { userSettingChanged } from "./events/other";
import { setAmplitudeUserProperties, trackAmplitude } from "./impl/amplitude";
import { pushFaroMeasurement } from "./impl/faro";
import { normalizeEventData } from "./normalize";
import type { AnalyticsEvent, UserPropertyValue } from "./types";

export function trackEvent(params: AnalyticsEvent) {
  const { event, ...data } = params;
  try {
    trackAmplitude(event, normalizeEventData(data));
  } catch (e) {
    captureException(e, { event: { params } });
  }
}

export function trackUserSettingsChanged(newSettings: ClientSettings, oldSettings: ClientSettings) {
  // Send events to amplitude when any _tracked_ user setting is changed
  for (const key in newSettings) {
    // Only track the updated setting, and only if it's in trackedSettings
    if (isTrackedSettingsKey(key) && newSettings[key] !== oldSettings[key]) {
      trackEvent(
        userSettingChanged({ key, fromValue: oldSettings[key], toValue: newSettings[key] }),
      );
      trackUserProperty(key, newSettings[key]);
    }
  }
}

export function trackUserSettings() {
  const settings = useClientSettingsStore();
  const entries = Array<[string, UserPropertyValue]>();
  for (const key in settings) {
    if (isTrackedSettingsKey(key)) {
      entries.push([key, settings[key]]);
    }
  }
  setAmplitudeUserProperties(entries);
}

export function trackUserProperty(property: CustomUserProperty, value: UserPropertyValue) {
  setAmplitudeUserProperties([[property, value]]);
}

export function trackMeasurement(measurement: Omit<MeasurementEvent, "timestamp">) {
  pushFaroMeasurement(measurement);
}

type CustomUserProperty =
  | "company"
  | "role"
  | "plan"
  | "language"
  | keyof ClientSettings
  | "workingMode";

/**
 * ClientSettings we want to track.
 * Excluding
 *  - workingMode (already tracked separately)
 *  - defaultStickyType (difficult to track as it's per-board)
 */
const trackedSettings = [
  "stickyFont",
  "inputDevice",
  "effectiveInputDevice", // Tracking this to measure the effectiveness of the heuristics -> we don't want to see it switching back and forth much
  "overload",
  "permanentTeamRiskCanvasLinks",
  "permanentProgramLinks",
  "initialLineupDistance",
  "stackDistanceX",
  "stackDistanceY",
  "showStats",
  "overviewScale",
  "highContrastMode",
  "textAlignment",
  "archivedSessions",
] as const;
type TrackedSettingsKey = (typeof trackedSettings)[number];

function isTrackedSettingsKey(key: string): key is TrackedSettingsKey {
  return trackedSettings.includes(key);
}
